"use client"

import * as React from "react"
import { ArrowDown, ArrowUp } from "lucide-react"
import { tableClass } from "../lib/table.ts"
import type { CellAlign } from "../lib/table.ts"
import { cn } from "../lib/utils.ts"

/**
 * The table parts for a small table built by hand (a breakdown in a drawer, a settings
 * matrix). They carry the same defaults as DataTable: small dim headers, row-height rows with
 * a thin divider and a hover tint, muted cells with a bright name, numbers right in `num`.
 */
function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      data-list=""
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom border-collapse text-left text-ui", className)}
        {...props}
      />
    </div>
  )
}

/** `sticky` keeps the header on top while the table scrolls (give it the surface it sits on). */
function TableHeader({ className, sticky, ...props }: React.ComponentProps<"thead"> & { sticky?: boolean }) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "[&_tr]:border-b [&_tr]:border-border-subtle [&_tr]:hover:bg-transparent",
        sticky && "sticky top-0 z-(--z-sticky) bg-surface",
        className
      )}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

/** Totals and other summary rows: dim, under a divider. */
function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t border-border-subtle text-fg-subtle [&_tr]:border-0 [&_tr]:hover:bg-transparent [&_td]:text-fg-subtle",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        tableClass.row,
        "has-aria-expanded:bg-hover data-[state=selected]:bg-active",
        className
      )}
      {...props}
    />
  )
}

type SortDir = "asc" | "desc" | null

interface AlignProps {
  /** Right for numbers; `numeric` says the same and adds tabular figures. */
  align?: CellAlign
  numeric?: boolean
}

const alignOf = ({ align, numeric }: AlignProps): CellAlign => align ?? (numeric ? "right" : "left")

/**
 * A header cell. With `onSort` it is a sort control: a button with a small arrow for the
 * direction, and `aria-sort` on the cell while it is the sorted column.
 */
function TableHead({
  className,
  align,
  numeric,
  sort,
  onSort,
  children,
  ...props
}: React.ComponentProps<"th"> & AlignProps & { sort?: SortDir; onSort?: () => void }) {
  const right = alignOf({ align, numeric }) === "right"
  return (
    <th
      data-slot="table-head"
      scope="col"
      aria-sort={onSort && sort ? (sort === "asc" ? "ascending" : "descending") : undefined}
      className={cn(
        tableClass.head,
        "px-3 first:pl-row-x last:pr-row-x [&:has([role=checkbox])]:pr-0",
        right ? "text-right" : "text-left",
        className
      )}
      {...props}
    >
      {onSort ? (
        <TableSortButton dir={sort ?? null} right={right} onClick={onSort}>
          {children}
        </TableSortButton>
      ) : (
        children
      )}
    </th>
  )
}

/** `primary` marks the row's name: bright and 500. Every other cell is muted. */
function TableCell({ className, align, numeric, primary, ...props }: React.ComponentProps<"td"> & AlignProps & { primary?: boolean }) {
  const right = alignOf({ align, numeric }) === "right"
  return (
    <td
      data-slot="table-cell"
      className={cn(
        tableClass.cell,
        "px-3 whitespace-nowrap first:pl-row-x last:pr-row-x [&:has([role=checkbox])]:pr-0",
        right && "text-right",
        numeric && "num",
        primary && tableClass.name,
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-2 text-left text-caption text-fg-subtle", className)}
      {...props}
    />
  )
}

/**
 * The sort control inside a header cell. The arrow shows the direction while sorted and hints
 * on hover; in a right-aligned column it sits before the label so the label keeps the edge.
 */
function TableSortButton({
  dir,
  right,
  onClick,
  children,
}: {
  dir: SortDir
  right?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  const Icon = dir === "asc" ? ArrowUp : ArrowDown
  return (
    <button
      type="button"
      onClick={onClick}
      data-sort={dir ?? undefined}
      className={cn(
        "group/sort inline-flex h-control-xs items-center gap-1 rounded-control font-medium transition-colors duration-(--duration-fast) ease-standard hover:text-fg-muted",
        right && "flex-row-reverse",
        dir && "text-fg-muted"
      )}
    >
      {children}
      <Icon
        className={cn(
          "size-3 shrink-0 transition-opacity duration-(--duration-fast)",
          dir ? "opacity-100" : "opacity-0 group-hover/sort:opacity-60 group-focus-visible/sort:opacity-60"
        )}
        aria-hidden
      />
    </button>
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableSortButton,
}
